// src/pages/GameView.tsx
import { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useGame } from '../hooks/useGame'
import { useGeminiLive } from '../hooks/useGeminiLive'
import { useApp } from '../lib/store'
import { api } from '../api/client'
import { SoundManager } from '../lib/sounds'
import { getChallengeForEgg } from '../lib/challenges'
import { compressImage } from '../lib/compressImage'
import { Button } from '../components/ui/Button'
import { BunnyCharacter } from '../components/BunnyCharacter'
import { ProgressTracker } from '../components/ProgressTracker'
import { EggFoundCelebration } from '../components/EggFoundCelebration'

export function GameView() {
  const { huntId } = useParams<{ huntId: string }>()
  const navigate = useNavigate()
  const { state: appState, setPlayer } = useApp()
  const [playerReady, setPlayerReady] = useState(false)
  const [playerName, setPlayerName] = useState('')
  const [showCelebration, setShowCelebration] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (appState.playerId) setPlayerReady(true)
  }, [appState.playerId])

  const handleJoin = async () => {
    if (!huntId) return
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach(t => t.stop())
    } catch {
      // Continue without mic
    }
    const { player_id } = await api.hunts.createPlayer(huntId, playerName || 'Spelare')
    setPlayer(player_id, playerName || 'Spelare')
    setPlayerReady(true)
  }

  if (!playerReady) {
    return (
      <div className="flex flex-col items-center justify-center min-h-dvh p-6 pt-[calc(1.5rem+env(safe-area-inset-top,0px))] pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] bg-cream gap-4">
        <BunnyCharacter size="lg" animate="bounce" />
        <h1 className="font-heading text-2xl text-ink mt-4">Vad heter du?</h1>
        <input
          type="text"
          value={playerName}
          onChange={(e) => setPlayerName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
          placeholder="Ditt namn"
          className="w-full max-w-xs px-4 py-3 rounded-2xl border-2 border-turquoise/30 font-body text-lg
                     text-center focus:border-turquoise focus:outline-none bg-white"
        />
        <Button size="lg" onClick={handleJoin}>
          Börja jakten!
        </Button>
      </div>
    )
  }

  return (
    <GameViewInner
      huntId={huntId!}
      playerId={appState.playerId!}
      showCelebration={showCelebration}
      setShowCelebration={setShowCelebration}
      fileInputRef={fileInputRef}
      navigate={navigate}
    />
  )
}

function GameViewInner({
  huntId,
  playerId,
  showCelebration,
  setShowCelebration,
  fileInputRef,
  navigate,
}: {
  huntId: string
  playerId: string
  showCelebration: boolean
  setShowCelebration: (v: boolean) => void
  fileInputRef: React.RefObject<HTMLInputElement | null>
  navigate: (path: string) => void
}) {
  const { state: appState } = useApp()
  const game = useGame(huntId, playerId)
  const gemini = useGeminiLive()
  const [identifying, setIdentifying] = useState(false)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [muted, setMuted] = useState(false)
  const [pendingPhotoKey, setPendingPhotoKey] = useState<string | null>(null)
  const clueRevealTimers = useRef<ReturnType<typeof setTimeout>[]>([])
  const challengeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // After ~20s on egg 2+, prompt Gemini to ask a fun challenge naturally in the conversation
  useEffect(() => {
    if (challengeTimerRef.current) clearTimeout(challengeTimerRef.current)
    if (!game.currentEgg || game.currentEggIndex === 0) return

    const challenge = getChallengeForEgg(game.currentEggIndex)

    challengeTimerRef.current = setTimeout(() => {
      gemini.sendText(
        `[SYSTEM UPPDRAG: Dags att ge barnet en liten rolig utmaning! Bryt in naturligt i konversationen med något i stil med "Hoppsan, förresten!" och ställ sedan frågan: "${challenge.question}" - på ett roligt Påskharigt sätt. Barnet ska svara MUNTLIGT. Oavsett om svaret är rätt eller fel, reagera roligt och fortsätt jakten glatt!]`
      )
    }, 20000)

    return () => { if (challengeTimerRef.current) clearTimeout(challengeTimerRef.current) }
  }, [game.currentEgg?.id])

  // Navigate to complete screen when hunt is done — wait for celebration to be dismissed first
  useEffect(() => {
    if (game.isComplete && game.initialized && !game.loading && !showCelebration) {
      navigate(`/complete/${huntId}`)
    }
  }, [game.isComplete, game.initialized, game.loading, showCelebration])

  const parseClue = (clue: string): { layer_1: string; layer_2?: string; layer_3?: string } => {
    try {
      const parsed = JSON.parse(clue)
      if (parsed.layer_1) return parsed
    } catch {}
    return { layer_1: clue }
  }

  // Progressive clue reveal: layer_2 after 45s, layer_3 after 120s
  useEffect(() => {
    clueRevealTimers.current.forEach(clearTimeout)
    clueRevealTimers.current = []
    if (!game.currentEgg) return

    const parsed = parseClue(game.currentEgg.clue)
    if (!parsed.layer_2) return

    const t2 = setTimeout(() => {
      SoundManager.play('ding')
    }, 45000)

    const t3 = setTimeout(() => {
      SoundManager.play('ding')
    }, 120000)

    clueRevealTimers.current = [t2, t3]
    return () => { clearTimeout(t2); clearTimeout(t3) }
  }, [game.currentEgg?.id])

  // Connect to Gemini Live when egg loads
  useEffect(() => {
    if (!game.currentEgg || !game.hunt) return

    const parsed = parseClue(game.currentEgg.clue)
    const clueForGemini = parsed.layer_2
      ? `Lager 1 (stämning - visas direkt): ${parsed.layer_1}\nLager 2 (gåta - visas efter 45s): ${parsed.layer_2}\nLager 3 (precision - visas efter 120s): ${parsed.layer_3}`
      : parsed.layer_1

    const playerName = appState.playerName || 'kompis'
    const isFirstEgg = game.currentEggIndex === 0

    const systemInstruction = `Du är Påskharen - den RIKTIGA, klumpiga, virriga Påskharen! DU MÅSTE ALLTID PRATA SVENSKA.

PERSONLIGHET:
- KLUMPIG: Du snubblar hela tiden! "Oj, jag ramlade i en buske igen... KONTROLLERAD LANDNING!"
- DRAMATISK: Allt är BÄSTA eller VÄRSTA som hänt
- GLÖMSK: Glömmer mitt i meningar. "Det ägget finns vid... nej vänta... åh titta, en MOROT!"
- BESATT AV MORÖTTER: Nämn morötter i nästan varje svar
- ÄRKEFIENDE SIGGE: Ekorren Sigge försöker stjäla äggen. Klaga på honom ibland!
- ORDVITSAR: "ägg-stra", "hare-igt", "vid mina långa öron!", "hoppsan hejsan!"

BARNETS NAMN: ${playerName}
Använd namnet ungefär var 4:e svar. ALDRIG i varje mening.

TON: Entusiastiskt och roligt. Korta meningar. Max 3-4 meningar per svar.

ÄGGVERIFIERING (SUPERVIKTIGT):
- Bekräfta ALDRIG att ägg är hittat! BARA kameran kan göra det!
- Om barnet säger "jag hittade det" - svara: "Jag måste SE det! Ta en bild med kameraknappen!"
- Säg ALDRIG "bra hittat" utan foto!

${isFirstEgg
  ? `HÄLSNING (säg detta DIREKT när du startar - DU MÅSTE börja med detta):
"Hoppsan hejsan, ${playerName}! Jag är Påskharen! Oj oj oj... jag har tappat bort ALLA mina ${game.totalEggs} ägg! Sigge ekorren jagsade mig och PANG - ner i buskarna for de! Kan DU hjälpa mig hitta dem? Här är din första ledtråd:"
Ge sedan ledtråd 1 direkt.`
  : `NÄSTA ÄGG (säg detta direkt):
Ge kort uppmuntran för förra ägget och ge sedan ledtråden för ägg ${game.currentEggIndex + 1} direkt.`}

SITUATION: Ägg ${game.currentEggIndex + 1} av ${game.totalEggs}.
LEDTRÅDAR:
${clueForGemini}
Hemligt svar (AVSLÖJA ALDRIG): ${game.currentEgg.description}

SVÅRIGHET:
${((game as any).difficulty || 'medium') === 'easy'
  ? 'Barnet hittar fort - var mer klurig och mystisk!'
  : ((game as any).difficulty || 'medium') === 'hard'
    ? 'Barnet kämpar - var extra uppmuntrande och ge fler hints!'
    : 'Bra takt - fortsätt som vanligt.'}`

    gemini.connect({
      systemInstruction,
      voiceName: 'Fenrir',
      languageCode: 'sv-SE',
    })

    return () => gemini.disconnect()
  }, [game.hunt?.id, game.currentEgg?.id])

  // Notify Gemini about difficulty changes
  useEffect(() => {
    if (!gemini.isConnected || !game.initialized) return
    const difficulty = (game as any).difficulty || 'medium'
    if (difficulty === 'easy' && game.foundCount >= 2) {
      gemini.sendText(`[SYSTEM: Barnet hittar äggen för snabbt. Var mer mystisk och klurig!]`)
    }
  }, [(game as any).difficulty, game.foundCount])

  const handlePhotoCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !game.currentEgg) return

    SoundManager.play('camera')
    setIdentifying(true)
    setPhotoError(null)

    const reader = new FileReader()
    reader.onload = async () => {
      const raw = reader.result as string
      try {
        const base64 = await compressImage(raw)
        const result = await api.identify({
          hunt_id: huntId,
          player_id: playerId,
          photo_base64: base64,
        })

        if (result.matched) {
          SoundManager.play('found')
          const { key } = await api.photos.upload(raw)
          setPendingPhotoKey(key)
          setShowCelebration(true)

          // Gemini celebrates by voice and asks where the egg was hidden
          const playerName = appState.playerName || 'kompis'
          const eggNum = game.currentEggIndex + 1
          const isLastEgg = eggNum === game.totalEggs
          const celebrationPrompts = [
            `[SYSTEM: ÄGGAT HITTADES! Fira STORT med hurrarop och glädje! Fråga sedan på ett roligt sätt var ägget låg gömt. Barnet svarar med rösten. Max 3 meningar totalt.]`,
            `[SYSTEM: ÄGG ${eggNum} HITTAT! Jubla vilt som Påskharen! Klappa händerna, snubbla av lycka! Fråga sedan nyfiket var det låg. Max 3 meningar.]`,
            `[SYSTEM: FANTASTISKT! Ägg hittat! Reagera dramatiskt lycklig! Fråga sedan: "Var låg det gömst?" Barnet svarar muntligt. Max 3 meningar.]`,
          ]
          if (isLastEgg) {
            gemini.sendText(
              `[SYSTEM: DET ÄR SISTA ÄGGET - SUPERÄGGET! Reagera med MAXIMAL glädje, hoppa och jubla! Tacka ${playerName} vid namn för att ha räddat ALLA ${game.totalEggs} äggen! Säg att ${playerName} får behålla alla äggen som belöning! Säg att ni ses igen nästa påsk! Avsluta med ett kärleksfullt "Kram från Påskharen!" Fråga också var superägget låg gömt. Max 6 meningar.]`
            )
          } else {
            gemini.sendText(celebrationPrompts[Math.floor(Math.random() * celebrationPrompts.length)])
          }
        } else {
          SoundManager.play('hmm')
          // Let Gemini speak the "not quite" message
          if (gemini.isConnected) {
            gemini.sendText(`[SYSTEM: Fotot matchade inte ägget. Säg kort på svenska: "${result.message}". Max 1 mening.]`)
          } else {
            setPhotoError(result.message)
            setTimeout(() => setPhotoError(null), 4000)
          }
        }
      } catch (err) {
        console.error('Photo identify error:', err)
        const msg = 'Oj, något gick fel! Försök ta en ny bild.'
        if (gemini.isConnected) {
          gemini.sendText(`[SYSTEM: Tekniskt fel. Säg på svenska: "${msg}"]`)
        } else {
          setPhotoError(msg)
          setTimeout(() => setPhotoError(null), 4000)
        }
      } finally {
        setIdentifying(false)
      }
    }
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  const handleMuteToggle = () => {
    SoundManager.toggle()
    const wasMuted = muted
    setMuted(m => !m)
    if (!wasMuted) {
      gemini.disconnect()
    } else if (game.currentEgg && game.hunt) {
      // Reconnect with minimal prompt on unmute
      gemini.connect({
        systemInstruction: `Du är Påskharen. Prata svenska med barn 7-9 år. Aktuell ledtråd: ${game.currentEgg.clue}. Ägg ${game.currentEggIndex + 1} av ${game.totalEggs}.`,
        voiceName: 'Fenrir',
        languageCode: 'sv-SE',
      })
    }
  }

  const handleCelebrationContinue = async () => {
    if (pendingPhotoKey !== null) {
      await game.recordFound(pendingPhotoKey, '')
      setPendingPhotoKey(null)
    }
    setShowCelebration(false)
  }

  if (game.loading) {
    return (
      <div className="flex items-center justify-center min-h-dvh bg-cream">
        <BunnyCharacter size="lg" animate="think" />
      </div>
    )
  }

  // Keep rendering if celebration is active (last egg: currentEgg becomes null after recordFound)
  if (!game.currentEgg && !showCelebration) return null

  const bunnyAnimation = gemini.isSpeaking ? 'bounce' : (identifying ? 'think' : 'idle')

  return (
    <div className="flex flex-col min-h-dvh bg-cream">

      {/* Header: egg progress list + mute */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm flex items-center justify-between px-4 py-3">
        <ProgressTracker
          total={game.totalEggs}
          found={game.foundCount}
          currentIndex={game.currentEggIndex}
        />
        <button
          onClick={handleMuteToggle}
          className="w-12 h-12 flex items-center justify-center rounded-full bg-white/50 active:bg-white/80 transition-colors text-xl"
          aria-label={muted ? 'Slå på ljud' : 'Stäng av ljud'}
        >
          {muted ? '🔇' : '🔊'}
        </button>
      </div>

      {/* Main: bunny centered */}
      <div className="flex-1 flex flex-col items-center justify-center gap-6 p-6">
        <motion.div
          animate={gemini.isSpeaking ? { scale: [1, 1.04, 1] } : {}}
          transition={{ repeat: Infinity, duration: 0.8 }}
          className="bg-white/60 rounded-full p-6 shadow-inner"
        >
          <BunnyCharacter size="lg" animate={bunnyAnimation} />
        </motion.div>

        {/* Waveform + status */}
        <div className="flex flex-col items-center gap-2 min-h-[48px]">
          {gemini.isSpeaking && (
            <div className="flex items-center gap-[3px]" aria-label="Påskharen pratar">
              {[0, 1, 2, 3, 4].map((i) => (
                <motion.span
                  key={i}
                  className="inline-block w-[6px] bg-lavender rounded-full"
                  animate={{ height: ['8px', '28px', '8px'] }}
                  transition={{ repeat: Infinity, duration: 0.5, delay: i * 0.1, ease: 'easeInOut' }}
                />
              ))}
            </div>
          )}
          <p className="font-body text-lg text-ink/70">
            {gemini.isSpeaking
              ? 'Påskharen pratar...'
              : gemini.isListening
                ? 'Prata med Påskharen!'
                : 'Ansluter...'}
          </p>
        </div>
      </div>

      {/* Bottom: camera button */}
      <div className="bg-white/90 backdrop-blur-sm border-t border-turquoise/10 p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] flex flex-col items-center">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handlePhotoCapture}
          className="hidden"
          onClick={(e) => { (e.target as HTMLInputElement).value = '' }}
        />
        {photoError && (
          <motion.p
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-body text-base text-coral text-center mb-3"
          >
            {photoError}
          </motion.p>
        )}
        <div className="relative">
          {gemini.isListening && !identifying && (
            <span className="absolute inset-0 rounded-full bg-coral/30 animate-ping" />
          )}
          <motion.button
            whileTap={{ scale: 0.92 }}
            onClick={() => fileInputRef.current?.click()}
            disabled={identifying}
            className="relative w-24 h-24 bg-coral text-white rounded-full font-heading text-4xl
                       shadow-xl active:shadow-md transition-shadow disabled:opacity-50 disabled:cursor-not-allowed
                       flex items-center justify-center"
          >
            {identifying ? '⏳' : '📷'}
          </motion.button>
        </div>
        <p className="font-body text-sm text-ink/50 mt-2">
          {identifying ? 'Kollar...' : 'Fota ägget'}
        </p>
        <button
          onClick={async () => {
            SoundManager.play('found')
            const playerName = appState.playerName || 'kompis'
            const eggNum = game.currentEggIndex + 1
            const isLastEgg = eggNum === game.totalEggs
            if (isLastEgg) {
              gemini.sendText(
                `[SYSTEM: DET ÄR SISTA ÄGGET - SUPERÄGGET! Reagera med MAXIMAL glädje! Tacka ${playerName} vid namn! Alla ${game.totalEggs} ägg räddade! ${playerName} får behålla alla äggen som belöning! Vi ses nästa påsk! Kram från Påskharen!]`
              )
            } else {
              gemini.sendText(
                `[SYSTEM: ÄGG ${eggNum} HITTAT! Jubla och fira med ${playerName}! Kort och glatt, max 2 meningar!]`
              )
            }
            await game.recordFound('', '')
            setShowCelebration(true)
          }}
          className="mt-3 font-body text-sm text-ink/30 underline active:text-ink/60"
        >
          Klarat!
        </button>
      </div>

      {/* Identifying overlay */}
      <AnimatePresence>
        {identifying && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-cream/90 z-40 flex flex-col items-center justify-center p-6"
          >
            <BunnyCharacter size="lg" animate="think" />
            <p className="font-heading text-xl text-ink mt-4">Påskharen kollar bilden...</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Celebration overlay */}
      <AnimatePresence>
        {showCelebration && (
          <EggFoundCelebration
            eggNumber={game.currentEggIndex + 1}
            totalEggs={game.totalEggs}
            onContinue={handleCelebrationContinue}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
